import React from "react";
import styles from "./recommendation-product-card.module.scss";
import {
	StoreProductDetailsResponse,
	PaymentPlan,
} from "../../../../../../service/contract/solar-recommendation";
import { Money } from "../../../../../../model/common/money";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../../../../../stores/slices/get-solar-slice";

type Props = {
	product: StoreProductDetailsResponse;
	paymentPlan: PaymentPlan;
	onContinue: () => void;
};

export const RecommendationProductCard: React.FC<Props> = ({ product, paymentPlan, onContinue }) => {
	const dispatch = useDispatch();

	const firstImage = product.images?.[0]?.imageUrl;

	const getPriceDisplay = (): string => {
		if (paymentPlan === PaymentPlan.PayAsYouGo && product.payAsYouGoDetails) {
			const cost = product.payAsYouGoDetails.costPerKwh;
			return new Money(cost.currency, cost.amount).toLocaleString();
		}
		const price = product.priceBreakdown.price;
		return new Money(price.currency, price.amount).toLocaleString();
	};

	const getDurationRange = (): string | null => {
		const options = product.priceBreakdown.monthlyPaymentOptions;
		if (!options?.length) return null;

		const sorted = [...options].sort((a, b) => a.durationInMonths - b.durationInMonths);
		const first = sorted[0].durationInMonths;
		const lastPart = sorted.length > 1 ? ` — ${sorted[sorted.length - 1].durationInMonths}` : '';
		return `${first}${lastPart} Months Plan`;
	};

	const onCardClick = () => {
		// Only in case of pay as you go, we need to open the payment info page
		dispatch(setSelectedProduct(product));
		onContinue();
	};

	const durationRange = getDurationRange();

	return (
		<div className={styles.card} onClick={onCardClick}>
			<div className={styles.imageContainer}>
				{firstImage ? (
					<img src={firstImage} alt={product.productName} />
				) : (
					<div className={styles.imagePlaceholder}>No Image</div>
				)}
				<span className={styles.priceBadge}>{getPriceDisplay()}</span>
			</div>

			<div className={styles.cardBody}>
				<div className={styles.productName}>{product.productName}</div>

				<div className={styles.specsRow}>
					{product.activeDuration != null && (
						<>
							<span>Up to {product.activeDuration} Hours</span>
							<span className={styles.specDivider}>|</span>
						</>
					)}
					<span>{product.productCapacity}kVA</span>
					{durationRange && (
						<>
							<span className={styles.specDivider}>|</span>
							<span>{durationRange}</span>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
